import {
  ConsentIdentitySchema,
  IdentitySubmissionDto,
} from '@act/shared/data-transfer-objects';
import { useEffect } from 'react';
import { FormState, useForm, Control } from 'react-hook-form';
import { consentRequests } from 'redux/requests/consents.requests';
import { consentIdentitySuccess } from 'redux/slices/consent.slice';
import { useAppDispatch } from 'redux/store-hooks';
import { useApiCall } from 'redux/utils/api-hook';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import './consents.scss';

interface UseConsentIdentityFormProps {
  linkKey?: string; // only used for member facing consents
}

type IdentitySubmissionType = z.infer<typeof ConsentIdentitySchema>;

export const useConsentIdentityForm = ({
  linkKey,
}: UseConsentIdentityFormProps): {
  formState: FormState<IdentitySubmissionType>;
  onSubmit: () => Promise<void>; // Reverted to void since we'll use Redux state
  control: Control<IdentitySubmissionType>;
  watch: () => IdentitySubmissionType;
} => {
  const dispatch = useAppDispatch();
  const [
    identityFormResponse,
    _isSubmittingIdentityForm,
    submitIdentityForm,
    identityFormError,
  ] = useApiCall(consentRequests.postIdentityForm);
  const [
    identityResponse,
    _isSubmittingIdentity,
    submitIdentity,
    identityError,
  ] = useApiCall(consentRequests.postIdentity);

  const defaultValues: Partial<IdentitySubmissionType> = {
    name: undefined,
    dateOfBirth: undefined,
    email: undefined,
    line1: undefined,
    city: undefined,
    state: undefined,
    zip: undefined,
  };

  const { control, handleSubmit, setError, reset, formState, watch } = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: zodResolver(ConsentIdentitySchema),
  });

  useEffect(() => {
    reset();
  }, [linkKey]);

  useEffect(() => {
    const errors = identityFormError?.errors || identityError?.errors;
    if (!errors) return;

    if (errors?.length) {
      for (const error of errors) {
        const message = Object.values(error.constraints).join(' ');
        const type = Object.keys(error.constraints)[0];
        setError(error.property as keyof IdentitySubmissionType, {
          type: type,
          message: message,
        });
      }
    }
  }, [identityError, identityFormError]);

  const handleSubmission = async (data: IdentitySubmissionType) => {
    if (linkKey) {
      submitIdentityForm({
        params: null,
        body: { ...data, consentLinkKey: linkKey } as IdentitySubmissionDto,
      });
    } else {
      submitIdentity({
        params: null,
        body: { ...data } as IdentitySubmissionDto,
      });
    }
  };

  useEffect(() => {
    if (identityResponse) {
      dispatch(consentIdentitySuccess(identityResponse));
    }
  }, [identityResponse, dispatch]);

  useEffect(() => {
    if (identityFormResponse) {
      dispatch(consentIdentitySuccess(identityFormResponse));
    }
  }, [identityFormResponse, dispatch]);

  return {
    formState,
    onSubmit: handleSubmit(handleSubmission),
    control,
    watch,
  };
};
